import {
  type Box,
  boxCenter,
  type Point,
  type Rect,
  rotatePoint,
  type SceneElement,
} from "@cva/shared";

/** Resize handle: x/y direction (-1, 0, 1). */
export type Handle = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";

export const HANDLE_DIRECTIONS: Record<Handle, { sx: -1 | 0 | 1; sy: -1 | 0 | 1 }> = {
  nw: { sx: -1, sy: -1 },
  n: { sx: 0, sy: -1 },
  ne: { sx: 1, sy: -1 },
  e: { sx: 1, sy: 0 },
  se: { sx: 1, sy: 1 },
  s: { sx: 0, sy: 1 },
  sw: { sx: -1, sy: 1 },
  w: { sx: -1, sy: 0 },
};

export const CORNER_HANDLES: Handle[] = ["nw", "ne", "se", "sw"];
export const ALL_HANDLES: Handle[] = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

/** Which handles an element offers. */
export function handlesFor(element: SceneElement | undefined, multi: boolean): Handle[] {
  if (multi || !element) return CORNER_HANDLES;
  if (
    element.type === "group" ||
    element.type === "svg" ||
    element.type === "qr" ||
    element.type === "icon" ||
    element.type === "device"
  )
    return CORNER_HANDLES;
  // Tables: the height follows the content.
  if (element.type === "table") return ["e", "w"];
  if (element.type === "text")
    return element.autoHeight ? ["nw", "ne", "se", "sw", "e", "w"] : ALL_HANDLES;
  if (element.type === "shape" && element.shape === "line") return ["e", "w"];
  return ALL_HANDLES;
}

/** Whether corner dragging keeps the aspect ratio by default. */
export function keepsAspect(element: SceneElement | undefined, multi: boolean): boolean {
  if (multi || !element) return true;
  return element.type !== "shape";
}

/**
 * New box when dragging a handle of a (rotated) box to `pointer`.
 * The opposite side/corner stays fixed.
 */
export function resizeBox(
  box: Box,
  handle: Handle,
  pointer: Point,
  options: { keepAspect: boolean; minSize?: number; fromCenter?: boolean },
): Box {
  const min = options.minSize ?? 1;
  const { sx, sy } = HANDLE_DIRECTIONS[handle];
  const center = boxCenter(box);
  // Pointer in the box's local frame, relative to its centre.
  const local = rotatePoint(pointer, center, -box.rotation);
  const lx = local.x - center.x;
  const ly = local.y - center.y;
  const anchorX = options.fromCenter ? 0 : (-sx * box.width) / 2;
  const anchorY = options.fromCenter ? 0 : (-sy * box.height) / 2;
  const factor = options.fromCenter ? 2 : 1;
  let width = sx !== 0 ? Math.max(min, (lx - anchorX) * sx * factor) : box.width;
  let height = sy !== 0 ? Math.max(min, (ly - anchorY) * sy * factor) : box.height;
  if (options.keepAspect && sx !== 0 && sy !== 0) {
    const scale = Math.max(width / box.width, height / box.height);
    width = Math.max(min, box.width * scale);
    height = Math.max(min, box.height * scale);
  }
  const centerLocal = {
    x: options.fromCenter || sx === 0 ? 0 : anchorX + (sx * width) / 2,
    y: options.fromCenter || sy === 0 ? 0 : anchorY + (sy * height) / 2,
  };
  const newCenter = rotatePoint(
    { x: center.x + centerLocal.x, y: center.y + centerLocal.y },
    center,
    box.rotation,
  );
  return {
    x: newCenter.x - width / 2,
    y: newCenter.y - height / 2,
    width,
    height,
    rotation: box.rotation,
  };
}

/** Uniform scale of a rectangle around the corner opposite to `handle`. */
export function scaleRect(rect: Rect, handle: Handle, pointer: Point, min = 4) {
  const { sx, sy } = HANDLE_DIRECTIONS[handle];
  const anchor = {
    x: sx > 0 ? rect.x : rect.x + rect.width,
    y: sy > 0 ? rect.y : rect.y + rect.height,
  };
  const width = Math.max(min, (pointer.x - anchor.x) * (sx || 1));
  const height = Math.max(min, (pointer.y - anchor.y) * (sy || 1));
  const scale = Math.max(
    width / rect.width,
    height / rect.height,
    min / Math.max(rect.width, rect.height),
  );
  return { scale, anchor };
}

export function angleFrom(center: Point, point: Point): number {
  return (Math.atan2(point.y - center.y, point.x - center.x) * 180) / Math.PI;
}

/** Snaps rotation to 15° steps (shift) or gently to right angles. */
export function snapAngle(angle: number, fine: boolean): number {
  let value = ((angle % 360) + 360) % 360;
  if (fine) value = Math.round(value / 15) * 15;
  else {
    const nearest = Math.round(value / 45) * 45;
    if (Math.abs(nearest - value) < 3) value = nearest;
  }
  value = value % 360;
  return value > 180 ? value - 360 : value;
}

export function pointInBox(point: Point, box: Box): boolean {
  const local = rotatePoint(point, boxCenter(box), -box.rotation);
  return (
    local.x >= box.x &&
    local.x <= box.x + box.width &&
    local.y >= box.y &&
    local.y <= box.y + box.height
  );
}

export const HANDLE_CURSORS: Record<Handle, string> = {
  nw: "nwse-resize",
  se: "nwse-resize",
  ne: "nesw-resize",
  sw: "nesw-resize",
  n: "ns-resize",
  s: "ns-resize",
  e: "ew-resize",
  w: "ew-resize",
};

/** Cursor for a handle of a rotated box. */
export function cursorFor(handle: Handle, rotation: number): string {
  const order: Handle[] = ["n", "ne", "e", "se", "s", "sw", "w", "nw"];
  const steps = Math.round((((rotation % 360) + 360) % 360) / 45);
  const index = (order.indexOf(handle) + steps) % 8;
  return HANDLE_CURSORS[order[index]!];
}
